<script>
import BreadcrumbsWrapper from '@components/breadcrumbs/breadcrumbs-wrapper.vue';
import EditableText from '@components/editables/editable-text.vue';
import PhotoUploader from '@components/uploaders/photo-uploader.vue';
import { mask } from 'vue-the-mask';
import { counterMixin } from '@mixins/counter-mixin';
import resize from '@utils/image-sizer';

import {
  authComputed,
  exerciseDifficultyComputed,
  mediaComputed,
  muscleGroupComputed,
  workoutComputed,
  workoutMethods,
} from '@state/helpers';
import isEqual from 'lodash/isEqual';

export default {
  components: {
    BreadcrumbsWrapper,
    EditableText,
    PhotoUploader,
  },
  directives: { mask },
  props: {
    workout: {
      type: Object,
      required: false,
      default: () => {},
    },
    initSelectedWorkout: {
      type: Object,
      required: false,
      default: () => {},
    },
    type: {
      type: String,
      required: false,
      default: null,
    },
    inPanel: {
      type: Boolean,
      required: false,
    },
    allowEditing: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  mixins: [counterMixin],
  data() {
    return {
      newMuscleGroups: null,
      newDifficulty: null,
    };
  },
  computed: {
    ...authComputed,
    ...exerciseDifficultyComputed,
    ...mediaComputed,
    ...muscleGroupComputed,
    ...workoutComputed,
    coverPhoto() {
      return this.selectedWorkout &&
        this.selectedWorkout.mediaAssets &&
        this.selectedWorkout.mediaAssets.coverPhoto &&
        this.selectedWorkout.mediaAssets.coverPhoto.url
        ? resize(this.selectedWorkout.mediaAssets.coverPhoto.url, 1200, 276)
        : `https://org-${this.userOrgId}.s3.amazonaws.com/default-workout.jpg`;
    },
    selectedMuscleGroups() {
      const selectedMuscleGroups = this.selectedWorkout.muscleGroups;
      return this.newMuscleGroups || selectedMuscleGroups;
    },
    selectedDifficulty() {
      const selectedDifficulty = this.selectedWorkout.exerciseDifficulty;
      return (
        (this.newDifficulty && this.newDifficulty.label) ||
        (selectedDifficulty ? selectedDifficulty.label : selectedDifficulty)
      );
    },
    timeToComplete() {
      return this.selectedWorkout.timeToComplete
        ? this.selectedWorkout.timeToComplete > 60
          ? this.selectedWorkout.timeToComplete / 60
          : 0
        : null;
    },
    placeholderText() {
      return (this.selectedWorkout && this.selectedWorkout._id) ||
        (this.selectedWorkout.programWorkout_id &&
          !this.selectedWorkout.description)
        ? ''
        : 'Getting your strongest core in 3 workouts';
    },
  },
  mounted() {},
  methods: {
    ...workoutMethods,
    handleUpdateWorkoutProps(workout) {
      this.$emit('update-workout-props', workout);
    },
    updateWorkoutTitle(value) {
      this.setUpdateWorkoutTitle(value);
    },
    updateWorkoutDescription(value) {
      this.setUpdateWorkoutDescription(value);
    },
    updateWorkoutDuration(value) {
      const seconds = this.$_covertToSeconds(value + ':00');
      if (typeof seconds === 'number') {
        this.setUpdateWorkoutDuration(seconds);
      }
    },
    onSelectMuscleGroup(value) {
      if (this.selectedMuscleGroups && this.selectedMuscleGroups.length) {
        const matchesMuscleGroup = this.selectedMuscleGroups.find((group) => {
          if (group._id === value._id || group.muscleGroup_id === value._id) {
            return group;
          }
        });

        if (matchesMuscleGroup) {
          this.setRemoveWorkoutMuscleGroup(value._id);
        } else {
          this.setUpdateWorkoutMuscleGroup(value);
        }
      } else {
        this.setUpdateWorkoutMuscleGroup(value);
      }
    },
    isSelectedMuscleGroup(id) {
      if (this.selectedMuscleGroups && this.selectedMuscleGroups.length) {
        const matchesMuscleGroup = this.selectedMuscleGroups.find((group) => {
          if (group._id === id || group.muscleGroup_id === id) {
            return group;
          }
        });

        if (matchesMuscleGroup) {
          return true;
        }
      }
    },
    onSelectDifficulty(value) {
      this.newDifficulty = value;
      this.setUpdateWorkoutDifficulty(value);
    },
  },
  watch: {
    selectedWorkout: {
      deep: true,
      handler(n, o) {
        if (isEqual(this.selectedWorkout, this.initSelectedWorkout)) {
          this.newMuscleGroups = null;
          this.newDifficulty = null;
        }
      },
    },
  },
};
</script>

<template>
  <div :class="['row hero', type]">
    <div class="col">
      <BreadcrumbsWrapper v-if="!inPanel" prevPageName="Workouts" />

      <div class="hero-section">
        <h6 class="section-title">Workout Title</h6>
        <h1 class="hero-title">
          <EditableText
            v-if="selectedWorkout && allowEditing"
            :text="selectedWorkout.label"
            placeholderTxt="Enter Workout Name"
            mode="edit"
            :canEdit="true"
            @update-editable-value="updateWorkoutTitle"
          />
          <span v-if="selectedWorkout && !allowEditing">{{
            selectedWorkout.label
          }}</span>
        </h1>

        <h6 class="section-title">Description</h6>
        <BaseInputTextarea
          v-if="selectedWorkout"
          :disabled="!allowEditing"
          name="description"
          rows="5"
          :value="selectedWorkout.description"
          :placeholder="placeholderText"
          @change="updateWorkoutDescription($event.target.value)"
        />

        <div class="row">
          <div class="col-xl-6 col-lg-6 col-md-6">
            <label>Muscle Groups</label>
            <v-select
              multiple
              v-if="selectedWorkout"
              :class="['tdm-multi-select', { disabled: !allowEditing }]"
              :closeOnSelect="false"
              :options="allMuscleGroups"
              :searchable="false"
              :clearable="false"
              :value="selectedMuscleGroups"
            >
              <template v-slot:selected-option-container="{ option }">
                <div
                  v-if="selectedMuscleGroups && selectedMuscleGroups.length > 1"
                >
                  {{ selectedMuscleGroups.length }} Selected
                </div>
                <div v-else>{{ option.label }}</div>
              </template>
              <template v-slot:option="{ _id, label }">
                <div
                  class="option-label-wrapper"
                  @click="
                    allowEditing
                      ? onSelectMuscleGroup({ _id, label })
                      : () => {}
                  "
                >
                  <div>{{ label }}</div>
                  <BaseIcon
                    class="icon-check"
                    name="check"
                    v-if="isSelectedMuscleGroup(_id)"
                  />
                </div>
              </template>
            </v-select>
          </div>
          <div class="col-xl-6 col-lg-6 col-md-6">
            <label>Level</label>
            <v-select
              :disabled="!allowEditing"
              :class="{ disabled: !allowEditing }"
              v-if="selectedWorkout"
              :options="allExerciseDifficulties"
              :searchable="false"
              :clearable="false"
              :value="selectedDifficulty"
              @input="onSelectDifficulty"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-xl-6 col-lg-6 col-md-6 completion_time">
            <label>Time to Complete</label>
            <BaseInputText
              v-if="selectedWorkout"
              type="tel"
              name="completion_time"
              label="min"
              :value="timeToComplete"
              v-mask="[`###`]"
              placeholder="mmm"
              :disabled="!allowEditing"
              @change="updateWorkoutDuration($event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="col">
      <div class="hero-section">
        <div
          class="program-photo-wrapper"
          :style="{
            'background-image': `url(${tempPhotoPreview || coverPhoto})`,
          }"
          >&nbsp;</div
        >

        <div class="row">
          <div class="col">
            <PhotoUploader
              requestType="workouts"
              type="cover"
              :hidePlaceholder="true"
              :hidePhotoPreview="true"
              :hideButton="!allowEditing"
              :title="allowEditing ? 'Cover Photo' : ''"
              :helper-text="
                allowEditing
                  ? 'must be .jpg, .jpeg, .gif, or .webp, no larger than 4MB.'
                  : ''
              "
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';
// stylelint-disable selector-pseudo-element-no-unknown

.hero {
  justify-content: space-between;
  margin: 0 0 1.8rem;
  min-height: 60rem;
  padding: 3.2rem 6rem 3.7rem;

  .hero-section {
    flex: 1;
    label {
      white-space: nowrap;
    }
    .completion_time {
      flex: 0 0 calc(45% - 15px);
      max-width: calc(45% - 15px);
      ::v-deep input {
        &:disabled {
          background-color: white;
        }
      }
    }
    ::v-deep textarea {
      &:disabled {
        background-color: white;
        border: unset;
        resize: none;
      }
    }
    .v-select {
      &.disabled {
        ::v-deep .vs__dropdown-toggle {
          background-color: white !important;
          .vs__search {
            background-color: white !important;
          }
        }
        ::v-deep .vs__dropdown-menu {
          background-color: white !important;
          li {
            cursor: unset !important;
          }
        }
      }
    }
  }

  .description-wrapper {
    background: $color-off-white;
    border-radius: $base-border-radius;
    height: 9.5rem;
    margin-bottom: 3.7rem;
    overflow-y: scroll;
    padding: 1rem;
  }

  .program-photo-wrapper {
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: $base-border-radius-md;
    height: 27.6rem;
    width: 100%;
  }

  &.light {
    background: white;

    ::v-deep .input {
      background: $color-off-white;
    }
  }
}
</style>
